import React, { useEffect, useState, useRef, useMemo } from "react";
import { t } from "../i18n";
import "./MediaSelectorMini.css";

/**
 * MediaSelectorMini – compact inline media picker for segment images.
 *
 * Props:
 * - onSelect(url)         – called when a media is chosen (upload or library thumb)
 * - accept                – file accept string (default "image/*")
 * - pickerTarget          – target key for the media library picker
 * - pickerReturn          – return URL after picking in the library
 * - uploading             – external uploading flag (disables UI)
 */
export default function MediaSelectorMini({
    onSelect,
    accept = "image/*",
    pickerTarget = "",
    pickerReturn = "",
    uploading = false
}) {
    const [mediaItems, setMediaItems] = useState([]);
    const [localUploading, setLocalUploading] = useState(false);
    const fileRef = useRef(null);

    const isUploading = uploading || localUploading;

    // Load recent library images (first 6)
    useEffect(() => {
        let isMounted = true;
        fetch("/api/fast/media/assets")
            .then((res) => res.json())
            .then((data) => {
                if (!isMounted) return;
                const items = Array.isArray(data.items) ? data.items : [];
                const images = items
                    .filter((i) => (i.type || "").toLowerCase() === "image" && i.status !== "deleted")
                    .slice(0, 6)
                    .map((i) => ({
                        url: i.url,
                        thumbnail_url: i.thumbnail_url || ""
                    }));
                setMediaItems(images);
            })
            .catch(() => { });
        return () => { isMounted = false; };
    }, []);

    async function handleUpload(event) {
        const file = event.target.files?.[0];
        if (!file) return;
        setLocalUploading(true);
        try {
            const formData = new FormData();
            formData.append("file", file);
            formData.append("folder", "images");
            const res = await fetch("/api/fast/upload", { method: "POST", body: formData });
            const data = await res.json();
            if (!data.ok || !data.url) throw new Error(data.error || "Upload error");
            onSelect(data.url);
        } catch (err) {
            alert(err.message || t("segment.uploadError"));
        } finally {
            setLocalUploading(false);
            event.target.value = "";
        }
    }

    const pickerUrl = useMemo(() => {
        if (!pickerTarget || !pickerReturn) return "";
        const params = new URLSearchParams({
            picker: "1",
            target: pickerTarget,
            type: "image",
            multi: "0",
            return: pickerReturn
        });
        return `/media?${params.toString()}`;
    }, [pickerTarget, pickerReturn]);

    return (
        <div className="mini-selector">
            {/* Action buttons */}
            <div className="mini-selector-actions">
                {pickerUrl && (
                    <a className="mini-selector-btn" href={pickerUrl}>
                        <span className="material-symbols-rounded" style={{ fontSize: 14 }}>photo_library</span>
                        {t("mediasel.library")}
                    </a>
                )}
                <button
                    type="button"
                    className="mini-selector-btn"
                    onClick={() => fileRef.current?.click()}
                    disabled={isUploading}
                >
                    <span className="material-symbols-rounded" style={{ fontSize: 14 }}>
                        {isUploading ? "progress_activity" : "upload"}
                    </span>
                    {isUploading ? "Upload..." : t("mediasel.upload") || "Upload"}
                </button>
                <input
                    ref={fileRef}
                    type="file"
                    accept={accept}
                    onChange={handleUpload}
                    style={{ display: "none" }}
                />
            </div>

            {/* Quick thumbnails */}
            {mediaItems.length > 0 && (
                <div className="mini-selector-thumbs">
                    {mediaItems.map((item) => (
                        <button
                            type="button"
                            key={item.url}
                            className="mini-selector-thumb"
                            onClick={() => onSelect(item.url)}
                            title={t("mediasel.library")}
                        >
                            <img src={item.thumbnail_url || item.url} alt="" />
                        </button>
                    ))}
                </div>
            )}
        </div>
    );
}
